import { type CompanyFormat, formatMoney } from "@majitap/ui";
import type { ReactNode } from "react";
import { breakdown, type PriceLine, type SheetCell, sourceOf, type ZoneSheet } from "../../zones";
import styles from "./zones.module.css";

type Money = Pick<CompanyFormat, "currency">;

/** Where a price comes from: a dot and the zone's name when it's set here, "Any zone" otherwise. */
export function Source({ scope, zoneName }: { scope: PriceLine["scope"]; zoneName?: string | null }) {
  const { label, own } = sourceOf(scope, zoneName);
  return <span className={own ? styles.sourceOwn : styles.source}>{label}</span>;
}

/** A line's amount: money, or the rate for a percent charge. */
export function lineAmount(l: PriceLine, format: Money) {
  // "16.00" → "16%", "7.50" → "7.5%": trimmed as text, a rate never goes through a JS number
  return l.basis === "percent" && l.percent ? `${l.percent.replace(/\.?0+$/, "")}%` : formatMoney(l.amount, format);
}

/** "Bulk water · 5,000 litres" → the product, then the size under it (the design's price rows). */
export function itemParts(name: string): { product: string; size: string | null } {
  const at = name.indexOf(" · ");
  return at < 0 ? { product: name, size: null } : { product: name.slice(0, at), size: name.slice(at + 3) };
}

/**
 * The price of an item from a cell: its product_price line, or why there's none. Stacked (the
 * amount over its source) in a narrow column, side by side in a wide one.
 */
export function CellPrice({
  cell,
  zoneName,
  format,
  inline = false,
}: {
  cell: SheetCell | null;
  zoneName?: string;
  format: Money;
  inline?: boolean;
}) {
  if (!cell) return <span className={styles.mutedInline}>—</span>;
  if (cell.missing) return <span className={styles.warningText}>No price</span>;
  const { price } = breakdown(cell.lines);
  if (!price) return <span className={styles.mutedInline}>—</span>;
  return (
    <span className={inline ? styles.inlinePrice : styles.stackEnd}>
      <span className={styles.amount}>{formatMoney(price.amount, format)}</span>
      <Source scope={price.scope} zoneName={zoneName} />
    </span>
  );
}

function Row({ label, amount, source }: { label: ReactNode; amount: string; source?: ReactNode }) {
  return (
    <li className={styles.priceRow}>
      <span>{label}</span>
      {source ?? <span />}
      <span className={styles.amount}>{amount}</span>
    </li>
  );
}

/** What the customer pays for one item: its price, the other charges (delivery fee, VAT), the total. */
export function ChargesList({
  lines,
  receivable,
  zoneName,
  format,
}: {
  lines: PriceLine[];
  receivable: string;
  zoneName?: string;
  format: Money;
}) {
  const { price, charges } = breakdown(lines);
  return (
    <ul className={styles.priceList} aria-label="What the customer pays">
      {price ? (
        <Row
          label={price.charge_type.name}
          amount={lineAmount(price, format)}
          source={<Source scope={price.scope} zoneName={zoneName} />}
        />
      ) : null}
      {charges.map((l) => (
        <Row
          key={`${l.charge_type.id}-${l.pricing_id ?? l.voucher_id}`}
          label={l.charge_type.name}
          amount={lineAmount(l, format)}
          source={<Source scope={l.scope} zoneName={zoneName} />}
        />
      ))}
      <li className={styles.totalRow}>
        <span>Customer pays</span>
        <span className={styles.amount}>{formatMoney(receivable, format)}</span>
      </li>
    </ul>
  );
}

/** What one item costs MajiTap (haulage, fill-site water), and the margin left. */
export function CostsList({
  lines,
  margin,
  zoneName,
  format,
}: {
  lines: PriceLine[];
  margin: string;
  zoneName?: string;
  format: Money;
}) {
  const { costs } = breakdown(lines);
  return (
    <ul className={styles.priceList} aria-label="What it costs MajiTap">
      {costs.length === 0 ? (
        <li className={styles.mutedInline}>No costs set here.</li>
      ) : (
        costs.map((l) => (
          <Row
            key={`${l.charge_type.id}-${l.pricing_id}`}
            label={l.charge_type.name}
            amount={formatMoney(l.amount, format)}
            source={<Source scope={l.scope} zoneName={zoneName} />}
          />
        ))
      )}
      <li className={styles.marginRow}>
        <span>Margin</span>
        <span className={styles.amount}>{formatMoney(margin, format)}</span>
      </li>
    </ul>
  );
}

/**
 * What one item costs at a place or in a zone, one of it: the price, the other charges, what the
 * customer pays, then the costs and the margin.
 */
export function PriceBreakdown({
  lines,
  totals,
  zoneName,
  format,
}: {
  lines: PriceLine[];
  totals: { receivable: string; payable: string; margin: string };
  zoneName?: string;
  format: Money;
}) {
  return (
    <div className={styles.breakdown}>
      <ChargesList lines={lines} receivable={totals.receivable} zoneName={zoneName} format={format} />
      <CostsList lines={lines} margin={totals.margin} zoneName={zoneName} format={format} />
    </div>
  );
}

/**
 * Every item's price in a zone, from the sheet: what a customer there pays, item by item. `inline`:
 * one line per item, the source beside the amount (the list's cards); otherwise the product over
 * its size, and the amount over its source (the side panel).
 */
export function ZonePriceList({
  sheet,
  zoneId,
  zoneName,
  format,
  inline = false,
}: {
  sheet: ZoneSheet;
  zoneId: string;
  zoneName: string;
  format: Money;
  inline?: boolean;
}) {
  const zone = sheet.zones.find((z) => z.geofence_id === zoneId);
  if (!zone || sheet.items.length === 0) return <p className={styles.muted}>No items to price yet.</p>;
  return (
    <ul className={styles.priceList} aria-label={`Prices in ${zoneName}`}>
      {sheet.items.map((item) => {
        const cell = zone.items.find((c) => c.product_item_id === item.id) ?? null;
        const { product, size } = itemParts(item.name);
        return (
          <li key={item.id} className={inline ? styles.itemRowInline : styles.itemRow}>
            {inline ? (
              <span>{item.name}</span>
            ) : (
              <span className={styles.stack}>
                <span>{product}</span>
                {size ? <span className={styles.mutedInline}>{size}</span> : null}
              </span>
            )}
            <CellPrice cell={cell} zoneName={zoneName} format={format} inline={inline} />
          </li>
        );
      })}
    </ul>
  );
}
